import { Link } from '@tanstack/react-router'
import { formatDistanceToNowStrict } from 'date-fns'
import { ArrowRight, ChevronRight, Inbox, PencilLine } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ItemGroup, Item, ItemActions, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@/components/ui/item'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { StatusBadge } from '../components/badges'
import type { InboxItem, Submission } from '../types'
import { CalmIllustration } from './HeroArt'

const ago = (iso: string | null | undefined) => {
    if (!iso) return ''
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? '' : formatDistanceToNowStrict(d, { addSuffix: true })
}

/** A submission's human title; seeded titles often just repeat the form name. */
const label = (s: Submission) => (s.title && s.title !== s.form_name ? s.title : s.form_name || s.number)

interface Props {
    drafts: Submission[] | null
    recent: Submission[] | null
    inbox: InboxItem[] | null
    /** When nothing is pending, render a calm illustration instead of collapsing (desktop hero slot). */
    showIdle?: boolean
    className?: string
}

export function hasActivity(p: Pick<Props, 'drafts' | 'recent' | 'inbox'>): boolean {
    return Boolean(p.drafts?.length || p.recent?.length || p.inbox?.length)
}

export function ActivityPanel({ drafts, recent, inbox, showIdle, className }: Props) {
    const loading = drafts === null || recent === null || inbox === null

    if (loading) {
        if (!showIdle) return null
        return (
            <div className={cn('flex flex-col gap-4 p-5', className)} aria-hidden>
                <Skeleton className="h-4 w-36" />
                {[0, 1, 2].map((i) => (
                    <div key={i} className="flex items-center gap-3">
                        <Skeleton className="size-8 rounded-lg" />
                        <div className="flex flex-1 flex-col gap-1.5">
                            <Skeleton className="h-3.5 w-3/4" />
                            <Skeleton className="h-3 w-1/2" />
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    if (!hasActivity({ drafts, recent, inbox })) {
        if (!showIdle) return null
        return (
            <div className={cn('flex h-full flex-col items-center justify-center gap-2 p-6 text-center', className)}>
                <CalmIllustration className="w-44 max-w-full" />
                <p className="text-sm font-medium">You’re all caught up</p>
                <p className="max-w-56 text-sm text-muted-foreground">Requests you start and approvals for you will show up here.</p>
            </div>
        )
    }

    const blocks = [
        inbox.length > 0 && <WaitingOnYou key="inbox" items={inbox} />,
        drafts.length > 0 && <Drafts key="drafts" items={drafts} />,
        recent.length > 0 && <Recent key="recent" items={recent} />,
    ].filter(Boolean)

    return (
        <div className={cn('flex flex-col gap-1 p-2', className)}>
            {blocks.map((b, i) => (
                <div key={i} className="flex flex-col gap-1">
                    {i > 0 && <Separator className="my-1" />}
                    {b}
                </div>
            ))}
        </div>
    )
}

function BlockHeader({ id, title, count, action }: { id: string; title: string; count?: number; action?: React.ReactNode }) {
    return (
        <div className="flex min-h-8 items-center justify-between gap-2 px-3 pt-2">
            <h2 id={id} className="flex items-center gap-2 text-sm font-semibold">
                {title}
                {count !== undefined && <Badge variant="secondary" className="tabular-nums">{count}</Badge>}
            </h2>
            {action}
        </div>
    )
}

function WaitingOnYou({ items }: { items: InboxItem[] }) {
    return (
        <section aria-labelledby="home-waiting" className="flex flex-col gap-1 rounded-lg bg-primary/5 pb-1">
            <BlockHeader
                id="home-waiting"
                title="Waiting on you"
                count={items.length}
                action={
                    <Button asChild variant="link" size="sm" className="h-auto px-0">
                        <Link to="/inbox">Review all<ArrowRight data-icon="inline-end" /></Link>
                    </Button>
                }
            />
            <ItemGroup className="gap-0">
                {items.slice(0, 3).map((s) => (
                    <Item key={s.id} asChild size="xs">
                        <Link to="/submissions/$id" params={{ id: s.id }}>
                            <ItemMedia variant="icon" className="text-primary"><Inbox /></ItemMedia>
                            <ItemContent className="min-w-0">
                                <ItemTitle className="w-full truncate">{s.form_name}</ItemTitle>
                                <ItemDescription className="truncate text-xs">
                                    {s.step_name} · from {s.submitter_name || 'someone'}
                                </ItemDescription>
                            </ItemContent>
                            <ItemActions><ChevronRight className="size-4 text-muted-foreground" /></ItemActions>
                        </Link>
                    </Item>
                ))}
            </ItemGroup>
        </section>
    )
}

function Drafts({ items }: { items: Submission[] }) {
    return (
        <section aria-labelledby="home-drafts" className="flex flex-col gap-1">
            <BlockHeader id="home-drafts" title="Pick up where you left off" />
            <ItemGroup className="gap-0">
                {items.slice(0, 3).map((s) => (
                    <Item key={s.id} size="xs">
                        <ItemMedia variant="icon" className="text-muted-foreground"><PencilLine /></ItemMedia>
                        <ItemContent className="min-w-0">
                            <ItemTitle className="w-full truncate">{label(s)}</ItemTitle>
                            <ItemDescription className="truncate text-xs">{s.status === 'returned' ? 'Sent back for changes' : 'Draft'} · edited {ago(s.updated_at)}</ItemDescription>
                        </ItemContent>
                        <ItemActions>
                            <Button asChild size="sm" variant="outline">
                                <Link to="/submissions/$id/edit" params={{ id: s.id }} aria-label={`Continue ${label(s)}`}>
                                    Continue
                                </Link>
                            </Button>
                        </ItemActions>
                    </Item>
                ))}
            </ItemGroup>
            {items.length > 3 && (
                <Button asChild variant="link" size="sm" className="w-fit px-3">
                    <Link to="/submissions">See all drafts<ArrowRight data-icon="inline-end" /></Link>
                </Button>
            )}
        </section>
    )
}

function Recent({ items }: { items: Submission[] }) {
    return (
        <section aria-labelledby="home-recent" className="flex flex-col gap-1">
            <BlockHeader
                id="home-recent"
                title="Your recent requests"
                action={
                    <Button asChild variant="link" size="sm" className="h-auto px-0 text-muted-foreground">
                        <Link to="/submissions">View all</Link>
                    </Button>
                }
            />
            <ItemGroup className="gap-0">
                {items.slice(0, 3).map((s) => (
                    <Item key={s.id} asChild size="xs">
                        <Link to="/submissions/$id" params={{ id: s.id }}>
                            <ItemContent className="min-w-0">
                                <ItemTitle className="w-full truncate">{label(s)}</ItemTitle>
                                <ItemDescription className="truncate font-mono text-xs">
                                    {s.number}
                                    <span className="font-sans"> · {ago(s.submitted_at ?? s.updated_at)}</span>
                                </ItemDescription>
                            </ItemContent>
                            <ItemActions><StatusBadge status={s.status} /></ItemActions>
                        </Link>
                    </Item>
                ))}
            </ItemGroup>
        </section>
    )
}
